import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { InviteService } from '../../features/classes/services/invite.service';
import { AuthService } from '../services/auth/auth.service';

/**
 * Guard that handles post-auth user type setup.
 * If user_type is NULL, determines type based on invite presence:
 * - Has invite → setup as student, redirect to /student
 * - No invite → setup as teacher, continue to requested route
 */
export const setupGuard: CanActivateFn = async () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const inviteService = inject(InviteService);

  // Wait for user to be loaded
  const user = await firstValueFrom(auth.$user);

  if (!user) {
    // Not authenticated - let auth guard handle this
    return true;
  }

  // If user_type is already set, no setup needed
  if (user.user_type !== null) {
    return true;
  }

  // User needs setup - check for pending invite
  const inviteToken = inviteService.getPendingInviteToken();

  if (inviteToken) {
    // Has invite → setup as student
    const result = await auth.setupAsStudent(inviteToken);

    if (result.success) {
      // Clear the pending invite
      inviteService.clearPendingInvite();
      // Redirect to student dashboard
      router.navigate(['/student']);
      return false;
    } else {
      // Failed to setup - clear invalid invite and setup as teacher
      console.warn('Failed to setup as student:', result.error);
      inviteService.clearPendingInvite();
      // Fall through to teacher setup
    }
  }

  // No invite (or failed) → setup as teacher
  const result = await auth.setupAsTeacher();

  if (!result.success) {
    console.error('Failed to setup as teacher:', result.error);
    // Still allow through - the app will handle the null user_type
  }

  return true;
};
